import React, { useCallback, useEffect, useMemo, useState } from "react";
import AITextField from "../AITextField";
import { Box, Button as MuiButton, Stack, Typography, useTheme } from "@mui/material";
import Color from "../../modules/Color";
import Brand from "../../api/Brand";
import { useCookies } from "react-cookie";
import BottomButton from "../BottomButton";
import { getAppLanguage } from "../../utils/languageUtils";
import { useNavigate } from "react-router-dom";
import {
    normalizeAddress,
    normalizeBrandName,
} from "../../utils/formattingUtils";
import AddressField from "../AddressField";
import OnboardingShell from "../OnboardingShell";
import ImagePicker from "../ImagePicker";
import {
    getCloudinaryOriginalUrl,
    uploadToCloudinary,
} from "../../utils/cloudinaryUtils";
import ImageCropperModal from "../ImageCropperModal";
import ColoredIcon from "../ColoredIcon";
import DefaultIcon from "../../img/default_icon.svg";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { FONTS, FORM_LABEL_SX } from "../../constants/styles";

const BrandCreationNew = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [logo, setLogo] = useState<string | null | undefined>();
    const [logoPreview, setLogoPreview] = useState<string | undefined>();
    const [business, setBusiness] = useState<string | undefined>();

    const [logoUploading, setLogoUploading] = useState(false);
    const [cropOpen, setCropOpen] = useState(false);
    const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);

    const originalForCrop = useMemo(() => {
        return logoPreview ? getCloudinaryOriginalUrl(logoPreview) : null;
    }, [logoPreview]);

    const [nameTaken, setNameTaken] = useState(false);
    const [addressTaken, setAddressTaken] = useState(false);
    const [addressVisible, setAddressVisible] = useState(false);

    const colors: { color: Color; name: string }[] = useMemo(() => [
        { color: new Color(139, 105, 105), name: "Rosy Brown" },
        { color: new Color(205, 175, 149), name: "Peru" },
        { color: new Color(205, 155, 155), name: "Light Coral" },
        { color: new Color(255, 182, 193), name: "Light Pink" },
        { color: new Color(139, 102, 139), name: "Dark Orchid" },
        { color: new Color(205, 150, 205), name: "Orchid" },
        { color: new Color(139, 58, 98), name: "Dark Magenta" },
        { color: new Color(139, 35, 35), name: "Firebrick" },
        { color: new Color(139, 87, 66), name: "Saddle Brown" },
        { color: new Color(255, 130, 71), name: "Coral" },
        { color: new Color(69, 139, 116), name: "Sea Green" },
        { color: new Color(36, 163, 148), name: "Teal" },
        { color: new Color(66, 159, 169), name: "Cadet Blue" },
        { color: new Color(139, 134, 78), name: "Dark Khaki" },
        { color: new Color(255, 99, 71), name: "Tomato" },
        { color: new Color(139, 58, 58), name: "Indian Red" },
        { color: new Color(255, 0, 0), name: "Red" },
        { color: new Color(16, 78, 139), name: "Dark Blue" },
        { color: new Color(205, 85, 85), name: "Crimson" },
        { color: new Color(30, 144, 255), name: "Dodger Blue" },
        { color: new Color(238, 99, 99), name: "Light Salmon" },
    ], []);

    const [cookies, setCookie] = useCookies(["theme_color"]);
    const businessType = sessionStorage.getItem("businessName") || "";
    const promptLanguage = getAppLanguage();

    const initialColor = cookies.theme_color
        ? Color.fromHex(cookies.theme_color)
        : undefined;
    const [selectedColor, setSelectedColor] = useState<Color | undefined>(initialColor);

    const putBrandIntoSessionStorage = useCallback(() => {
        const brandData = {
            name,
            address: address,
            color: (selectedColor ?? colors[0].color).toJSON(),
            business,
            logo,
        };
        sessionStorage.setItem("brand", JSON.stringify(brandData));
    }, [name, address, selectedColor, colors, business, logo]);

    const validateName = useCallback(
        async (name: string): Promise<boolean> => {
            try {
                const res = await Brand.validate({ name: name.trim() });
                const taken = res.nameTaken ?? false;
                setNameTaken(taken ?? false);
                return taken;
            } catch (err) {
                console.error("Error validating name:", err);
                showSnackbar("Failed to validate name.");
                return true;
            }
        },
        [showSnackbar]
    );

    const validateAddress = useCallback(
        async (address: string) => {
            try {
                const res = await Brand.validate({ address });
                setAddressTaken(res.addressTaken ?? false);
            } catch (err) {
                console.error("Error validating address:", err);
                showSnackbar("Failed to validate address.");
            }
        },
        [showSnackbar]
    );

    const onNameBlurHandler = useCallback(
        async (overrideName?: string) => {
            const finalName = (overrideName ?? name).trim();
            if (!finalName) return;

            const taken = await validateName(finalName);

            if (!taken) {
                const suggestion = normalizeAddress(finalName);
                if (address === "") {
                    setAddress(suggestion);
                    await validateAddress(suggestion);
                }
                setAddressVisible(true);
            }
        },
        [name, address, validateName, validateAddress]
    );

    useEffect(() => {
        if (!selectedColor) return;
        if (selectedColor.toHex() !== cookies.theme_color)
            setCookie("theme_color", selectedColor.toHex());
    }, [selectedColor, cookies.theme_color, setCookie]);

    useEffect(() => {
        const business_loaded = sessionStorage.getItem("business");
        if (business_loaded === null) {
            window.location.href = "/creation/business";
            return;
        }
        setBusiness(business_loaded!);

        const savedBrand = sessionStorage.getItem("brand");
        if (savedBrand) {
            const { name, color, logo } = JSON.parse(savedBrand);
            setName(name);
            setSelectedColor(Color.fromJSON(color));
            if (logo) {
                // Verify the logo URL actually loads (may be stale Cloudinary 404)
                const img = new Image();
                img.onload = () => {
                    setLogo(logo);
                    setLogoPreview(logo);
                };
                img.onerror = () => {
                    setLogo(undefined);
                    setLogoPreview(undefined);
                };
                img.src = logo;
            }
            validateName(name);
        }
    }, [validateName]);

    useEffect(() => {
        if (name.trim() && address.trim() && !nameTaken && !addressTaken)
            putBrandIntoSessionStorage();
    }, [
        name,
        address,
        nameTaken,
        addressTaken,
        selectedColor,
        business,
        putBrandIntoSessionStorage,
    ]);

    const handleNameChange = (input: string) => {
        const normalized = normalizeBrandName(input);
        setName(normalized);
        setNameTaken(false);
    };

    const onCreate = useCallback(async () => {
        if (nameTaken || addressTaken) {
            return;
        }
        if (name.trim() === "") {
            return;
        }

        if (business === undefined) navigate("/creation/business");
        else navigate("/creation/animated");
    }, [nameTaken, addressTaken, business, name, navigate]);

    const saveAIGeneratedName = async (generatedName: string) => {
        handleNameChange(generatedName);
        await onNameBlurHandler(generatedName);
    };

    const isHttpUrl = (s: string) => {
        try {
            const u = new URL(s);
            return u.protocol === "http:" || u.protocol === "https:";
        } catch {
            return false;
        }
    };

    const isDataUrl = (s: string) => /^data:image\/[a-zA-Z]+;base64,/.test(s);

    const ensureDataUrl = (s: string) =>
        isDataUrl(s) ? s : `data:image/png;base64,${s}`;

    const onLogoUpload = async (input?: string) => {
        if (!input) return;

        if (isHttpUrl(input)) {
            setLogo(input);
            setLogoPreview(input);
            return;
        }

        setLogoUploading(true);
        try {
            const dataUrl = ensureDataUrl(input);
            const stagingUrl = await uploadToCloudinary(dataUrl);
            if (stagingUrl) {
                setCropSourceUrl(stagingUrl);
                setCropOpen(true);
            } else {
                console.error("Failed to upload logo to Cloudinary.");
            }
        } catch (error) {
            console.error("Error during logo upload:", error);
        } finally {
            setLogoUploading(false);
        }
    };

    const handleCropSave = (finalTransformedUrl: string) => {
        setLogo(finalTransformedUrl);
        setLogoPreview(finalTransformedUrl);
        setCropOpen(false);
        setCropSourceUrl(null);
    };

    const handleCropCancel = () => {
        setCropOpen(false);
        setCropSourceUrl(null);
    };

    return (
        <>
            <OnboardingShell
                variant="form"
                step={2}
                totalSteps={3}
                heroTitle="Create your brand"
                heroSubtitle="Name, color, and icon"
                bottomButton={
                    <BottomButton
                        disabled={
                            !name.trim() ||
                            !address.trim() ||
                            !selectedColor ||
                            nameTaken ||
                            addressTaken ||
                            logoUploading
                        }
                        onClick={onCreate}
                        color="primary"
                        sticky
                    >
                        Create My App
                    </BottomButton>
                }
            >
                <Stack
                    className="force-dynamic-translation"
                    spacing={2}
                    sx={{ width: "100%" }}
                >
                    <Block title="Name Your App:">
                        <AITextField
                            placeholder="Name"
                            value={name}
                            setText={saveAIGeneratedName}
                            onChange={handleNameChange}
                            onBlur={() => onNameBlurHandler()}
                            error={nameTaken}
                            helperText={
                                nameTaken
                                    ? "This name is already in use"
                                    : undefined
                            }
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLInputElement>
                            ) => {
                                if (e.key === "Enter") {
                                    e.currentTarget.blur();
                                }
                            }}
                            width="100%"
                            businessType={businessType}
                            language={promptLanguage}
                        />
                        <Typography sx={{ ...FORM_LABEL_SX, mt: "4px !important" }}>
                            This is the public name of your app
                        </Typography>
                        <Box
                            sx={{
                                maxHeight: addressVisible ? "300px" : "0px",
                                opacity: addressVisible ? 1 : 0,
                                transform: addressVisible
                                    ? "translateY(0px)"
                                    : "translateY(-10px)",
                                transition:
                                    "max-height 400ms ease, opacity 300ms ease, transform 300ms ease",
                                overflow: "hidden",
                            }}
                        >
                            <Block title="Choose your link:">
                                <AddressField
                                    placeholder="Your public address (e.g. yourname123)"
                                    value={address}
                                    onChange={(val) => {
                                        setAddress(val);
                                        setAddressTaken(false);
                                    }}
                                    onBlur={() => validateAddress(address)}
                                    error={addressTaken}
                                    helperText={
                                        addressTaken
                                            ? "This address is already taken"
                                            : undefined
                                    }
                                    sx={{ width: "100%" }}
                                />
                                <Typography
                                    sx={{
                                        ...FORM_LABEL_SX,
                                        mt: "4px !important",
                                    }}
                                >
                                    This is how people will find your app (e.g.
                                    https://yourbrand.jemloc.com)
                                </Typography>
                            </Block>
                        </Box>
                        <Stack alignItems="center" spacing={0.25}>
                            <Typography
                                sx={{
                                    fontFamily: FONTS.body,
                                    fontSize: "14px",
                                    color: "text.secondary",
                                }}
                            >
                                Now choose a color and an icon
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONTS.body,
                                    fontSize: "13px",
                                    color: "text.secondary",
                                    opacity: 0.7,
                                }}
                            >
                                You can always change them later
                            </Typography>
                        </Stack>
                    </Block>
                    <Block title="Pick Your Brand Color:">
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                overflowX: "auto",
                                overflowY: "hidden",
                                "&::-webkit-scrollbar": { display: "none" },
                                scrollbarWidth: "none",
                            }}
                        >
                            {colors.map(({ color, name: colorName }) => (
                                <ColorCircle
                                    key={color.toHex()}
                                    color={color}
                                    label={colorName}
                                    isSelected={selectedColor ? color.cmp(selectedColor) : false}
                                    onClick={(color) => {
                                        setSelectedColor(color);
                                    }}
                                />
                            ))}
                        </Stack>
                    </Block>
                    <Block title="Your App Icon:">
                        <Stack direction="row" justifyContent="space-around">
                            <Stack spacing={1}>
                                <Box
                                    width="96px"
                                    height="96px"
                                    borderRadius="22px"
                                    bgcolor={selectedColor?.toHex() ?? theme.palette.primary.main}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    onClick={() => {
                                        if (!logoPreview) return;
                                        setCropSourceUrl(
                                            originalForCrop ?? logoPreview
                                        );
                                        setCropOpen(true);
                                    }}
                                    sx={{
                                        boxShadow: `0 6px 24px ${selectedColor?.toHex() ?? theme.palette.primary.main}40`,
                                        transition:
                                            "background-color 300ms ease, box-shadow 300ms ease",
                                        cursor: logoPreview
                                            ? "pointer"
                                            : "default",
                                    }}
                                >
                                    {logoPreview ? (
                                        <Box
                                            width="100%"
                                            height="100%"
                                            borderRadius="22px"
                                            sx={{
                                                backgroundImage: `url(${logoPreview})`,
                                                backgroundPosition: "center",
                                                backgroundRepeat: "no-repeat",
                                                backgroundSize: "cover",
                                            }}
                                        />
                                    ) : (
                                        <ColoredIcon
                                            icon={DefaultIcon}
                                            size={64}
                                            color="white"
                                        />
                                    )}
                                </Box>
                                {name && (
                                    <Typography
                                        className="no-translation"
                                        sx={{
                                            fontFamily: FONTS.display,
                                            fontWeight: 600,
                                            fontSize: "20px",
                                            textAlign: "center",
                                            color: selectedColor?.toHex() ?? theme.palette.primary.main,
                                            transition: "color 300ms ease",
                                        }}
                                    >
                                        {name}
                                    </Typography>
                                )}
                            </Stack>
                            <ImagePicker
                                onUpload={onLogoUpload}
                                brandName={name}
                                businessType={businessType}
                                color={selectedColor ?? colors[0].color}
                            />
                        </Stack>
                    </Block>
                </Stack>
            </OnboardingShell>
            <ImageCropperModal
                open={cropOpen}
                src={cropSourceUrl}
                onCancel={handleCropCancel}
                onSave={handleCropSave}
                mode="square"
                outSize={{ w: 512, h: 512 }}
            />
        </>
    );
};

const Block = (props: { title: String; children?: React.ReactNode }) => {
    const { title, children } = props;

    return (
        <Stack spacing={1} paddingY={1} width="100%">
            <Typography
                sx={{
                    fontFamily: FONTS.display,
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "24px",
                    pb: 0.5,
                    color: "#141414",
                }}
            >
                {title}
            </Typography>
            {children}
        </Stack>
    );
};

const ColorCircle = (props: {
    color: Color;
    label: string;
    isSelected?: boolean;
    onClick?: (color: Color) => void;
}) => {
    const { color, label, isSelected, onClick } = props;

    return (
        <Stack alignItems="center" spacing={0.5} pb={2}>
            <MuiButton
                key={color.toHex()}
                aria-label={`Select color: ${label}`}
                onClick={() => onClick?.(color)}
                sx={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "36px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 0,
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        width: "54px",
                        height: "54px",
                        border: isSelected
                            ? `2px solid ${color.toHex()}`
                            : "1px solid #0000001A",
                        borderRadius: "36px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transition:
                            "border-color 200ms ease, border-width 200ms ease",
                    }}
                >
                    <Box
                        sx={{
                            width: isSelected ? "42px" : "32px",
                            height: isSelected ? "42px" : "32px",
                            bgcolor: color.toHex(),
                            borderRadius: "36px",
                            transition:
                                "width 200ms ease, height 200ms ease",
                        }}
                    />
                </Box>
            </MuiButton>
            <Typography
                fontSize="10px"
                lineHeight="12px"
                textAlign="center"
                color={isSelected ? "#141414" : "#141414A0"}
                fontWeight={isSelected ? 600 : 400}
                width="60px"
                sx={{ transition: "color 200ms ease" }}
            >
                {label}
            </Typography>
        </Stack>
    );
};

export default BrandCreationNew;
